import dayjs from 'dayjs';

import { daysBetweenYmd, formatDateToDateString, getOperationToday } from 'shared/lib/dateFormatter';

/** Range shown when the operator picks no period, in days: the past year up to today. */
export const DATE_WINDOW_DEFAULT_DAYS = 365;

/** A period limit as the operator reads it: '1 year', '2 years' or '90 days'. */
export const dateWindowLimitText = (days) => {
    if (days % 365 === 0) {
        const years = days / 365;
        return years === 1 ? '1 year' : `${years} years`;
    }
    return `${days} days`;
};

/** The period a search covers when the operator picks none: the past year up to today. */
export const defaultDateWindow = (today = getOperationToday()) => {
    const end = dayjs(today);
    return {
        start: formatDateToDateString(end.subtract(DATE_WINDOW_DEFAULT_DAYS, 'day').toDate()),
        end: formatDateToDateString(end.toDate()),
    };
};

/**
 * Tells the operator why the picked period cannot be searched (an end missing, reversed, too
 * long) before the search goes out; null when it can. The server checks the same rules.
 */
export const dateWindowError = (start, end, maxDays) => {
    if (!start || !end) return 'Please select both a start and an end date.';
    const span = daysBetweenYmd(start, end);
    if (Number.isNaN(span)) return 'Please enter valid dates.';
    if (span < 0) return 'End date must be on or after the start date.';
    if (span > maxDays) return `Date range cannot exceed ${dateWindowLimitText(maxDays)}.`;
    return null;
};

/** True when the operator picked both ends; otherwise the default period applies. */
export const isPickedWindow = ({ start, end } = {}) => Boolean(start && end);

/** The picked period as the server reads it; null when none is picked (the server uses its default). */
export const buildDateWindowPayload = ({ field, start, end } = {}, defaultField) =>
    isPickedWindow({ start, end })
        ? { date_field: field || defaultField, start_date: start, end_date: end }
        : null;
